import React, { useCallback, useMemo } from 'react'
import moment from 'moment'
import { ActivityDetail, UserPreference } from 'api'
import { ActivityListContainer, ActivityListEmptyPlaceholder, AdminClearingAlert } from './activityMonitorStyles'
import { RFQCard } from 'view/shared/card'
import { getNegotiationTimeLeft, getRFQTitle } from 'view/routes/requests/shared/utils'
import { Locale, Translation } from 'locale'
import { iconSet } from 'view/components/icons'

export interface ActivityListProps {
  allActivityDetails: ActivityDetail[]
  showAdminClearingAlert: boolean
  userPreference: UserPreference
  sortByTimeLeft: boolean
  onActivityClick: (negotiationId: string, ownOrder: boolean, rfqTitle?: string) => unknown
}

export const ActivityList: React.FunctionComponent<ActivityListProps> = props => {
  const { allActivityDetails, showAdminClearingAlert, userPreference, sortByTimeLeft, onActivityClick } = props

  const empty = useMemo(() => allActivityDetails.length === 0, [allActivityDetails])

  const getSortedActivityDetails = useCallback(() => {
    return allActivityDetails.sort((a, b) => {
      const aNegotiation = a.negotiation
      const bNegotiation = b.negotiation

      const aResponded = !!a.respondedStatus
      const bResponded = !!b.respondedStatus

      const aTimeLeft = getNegotiationTimeLeft(aNegotiation)
      const bTimeLeft = getNegotiationTimeLeft(bNegotiation)

      if (!sortByTimeLeft || (aTimeLeft === bTimeLeft && !aResponded && !bResponded)) {
        if (moment(aNegotiation.lastUpdatedAt).isSameOrAfter(moment(bNegotiation.lastUpdatedAt))) {
          return -1
        } else {
          return 1
        }
      } else if (aResponded) {
        return 1
      } else if (bResponded) {
        return -1
      } else if (!aTimeLeft) {
        return 1
      } else if (!bTimeLeft) {
        return -1
      } else if (aTimeLeft > bTimeLeft) {
        return 1
      } else if (aTimeLeft <= bTimeLeft) {
        return -1
      } else {
        return -1
      }
    })
  }, [allActivityDetails, sortByTimeLeft])

  return (
    <ActivityListContainer>
      {showAdminClearingAlert && (
        <AdminClearingAlert>
          {iconSet.warningIcon} {Locale.translate(Translation.SC_CLEARING_FAILED_TRADES)}
        </AdminClearingAlert>
      )}
      {empty ? (
        <ActivityListEmptyPlaceholder>{Locale.translate(Translation.SC_NO_ACTIVITIES)}</ActivityListEmptyPlaceholder>
      ) : (
        getSortedActivityDetails().map(activityDetail => {
          const { respondedStatus, ...negotiationDetail } = activityDetail
          return (
            <RFQCard
              key={activityDetail.negotiation.negotiationId}
              negotiationDetail={negotiationDetail}
              userPreference={userPreference}
              hideLogo={true}
              respondedStatus={respondedStatus}
              onClick={() =>
                onActivityClick(activityDetail.negotiation.negotiationId, activityDetail.ownOrder, getRFQTitle(activityDetail))
              }
            />
          )
        })
      )}
    </ActivityListContainer>
  )
}
